// One page of a list, from the state in the URL (?q=&sort=&dir=&page=&size=, and
// ?reports=&open= for the checkboxes). The loaders of the list pages run it on the
// server, so the HTML holds the rows for Google. ListView reads the same URL state.

import { planSearch } from './search.js';
import { listPage } from './db.js';
import { readFilters } from './filters.js';

export const PAGE_SIZES = [10, 25, 50, 100];

// The Companies page and /ui/api/companies. Each sortable field has an ORDERED index.
export const COMPANY_LIST = {
  kind: 'company',
  from: 'cvr',
  fields: ['name', 'navne', 'enhedsNummer', 'cvr', 'virksomhedsformkort', 'sidstOpdateret', 'stiftelsesDato', 'ophoersDato', 'cvrstatus'],
  defaultSort: { field: 'sidstOpdateret', desc: true },
  checkboxes: true,
};

/** The list state of the URL `params` (URLSearchParams). */
export function readListParams(params, { defaultSort, checkboxes = false } = {}) {
  return {
    q: params.get('q') ?? '',
    page: Math.max(1, Number(params.get('page')) || 1),
    size: PAGE_SIZES.includes(Number(params.get('size'))) ? Number(params.get('size')) : 25,
    sortField: params.has('sort') ? params.get('sort') : (defaultSort?.field ?? ''),
    desc: params.has('dir') ? params.get('dir') === 'desc' : (defaultSort?.desc ?? true),
    filters: checkboxes ? readFilters(params) : {},
    // A search drops the default sort without a note; a sort the user chose gets one.
    chosenSort: params.has('sort') || params.has('dir'),
  };
}

/** The note when kinedb could not apply the sort, or ''. */
export function sortNote({ order, sortApplied, filtered, q, chosenSort }) {
  if (!order || sortApplied || !(chosenSort || !q.trim())) return '';
  if (q.trim()) return `kinedb is too slow to sort this search by "${order.field}", so the list is not sorted.`;
  if (filtered) return `With a checkbox on, kinedb is too slow to sort by "${order.field}" in this direction, so the list is not sorted. Search first, or sort Opdateret or Stiftet newest first.`;
  return `kinedb has no ordered index for "${order.field}", so this list is not sorted. Search first, then sort.`;
}

/**
 * The rows of one list page `{items, hasNext, note, sql, error}`. A refused or failed
 * query comes back as `error` (a message), so the page still shows the search box.
 */
export async function loadList(url, { kind, from, fields, defaultSort, checkboxes, resolvePlan }) {
  const state = readListParams(url.searchParams, { defaultSort, checkboxes });
  try {
    let plan = planSearch(state.q, kind);
    if (resolvePlan) plan = await resolvePlan(plan);
    if (plan.kind === 'error') throw new Error(plan.message);
    const order = state.sortField ? { field: state.sortField, desc: state.desc } : undefined;
    const result = await listPage({ from, fields, plan, filters: state.filters, order, page: state.page, pageSize: state.size });
    return {
      items: result.items,
      hasNext: result.hasNext,
      note: sortNote({ order, sortApplied: result.sortApplied, filtered: result.filtered, q: state.q, chosenSort: state.chosenSort }),
      sql: result.sql,
      error: null,
    };
  } catch (error) {
    return { items: [], hasNext: false, note: '', sql: '', error: error?.message ?? String(error) };
  }
}
